import type { ClientMessage, ServerMessage } from '@ourcove/types';
import { useStore } from './store';

type Handler = (msg: ServerMessage) => void;

class WSClient {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  private backoff = 1000;
  private token: string | null = null;
  private closedByUser = false;

  connect(token: string): void {
    this.token = token;
    this.closedByUser = false;
    this.open();
  }

  private open(): void {
    if (!this.token) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${window.location.host}/ws`;
    // Fastify/ws accepts subprotocols; we use ['jwt', <token>].
    const ws = new WebSocket(url, ['jwt', this.token]);
    this.ws = ws;
    ws.onopen = () => {
      this.backoff = 1000;
      useStore.getState().setWsConnected(true);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as ServerMessage;
        for (const h of this.handlers) h(msg);
      } catch {}
    };
    ws.onclose = () => {
      useStore.getState().setWsConnected(false);
      if (this.closedByUser) return;
      setTimeout(() => this.open(), this.backoff);
      this.backoff = Math.min(this.backoff * 2, 30_000);
    };
    ws.onerror = () => ws.close();
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  on(h: Handler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  close(): void {
    this.closedByUser = true;
    this.ws?.close();
    this.ws = null;
  }
}

export const wsClient = new WSClient();
